import {ProcessedManufacturingBatch} from 'screens/Patients/LeadsProfile/main/overview/hooks/useManufacturingDetails'
import hasValue from 'utils/hasValue'
import {TableContainerForBatches} from './TableContainerForBatches'
import {
  ManufacturingItem,
  ManufacturingStatus,
} from 'screens/Patients/LeadsProfile/main/overview/types/GettingStarted.types'
import AntdButton from 'components/atom/Buttons/AntdButton'
import React, {useContext, useMemo} from 'react'
import {AuthContext} from 'context/AuthContext'
import {modeOptions} from '@constants/modeOptions.constants'
import dayjs from 'dayjs'
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom'
import useProfileBasePath from '@hooks/useProfileBasePath'
import {AllTreatmentPlanListItem} from 'screens/Patients/LeadsProfile/main/treatment/types/treatmentPlan.types'
import {Tooltip} from 'antd'
import useDispatchAction from '@hooks/useDispatchAction'
import {setInstructions} from 'redux/Slices/AppSlice/ProductionSetup/Production.slice'
import treatmentPlanStatusConstants from '@constants/treatmentPlanStatus.constants'
import {useSelector} from 'react-redux'
import {RootState} from 'redux/store'
import useActiveProfile from '@hooks/useActiveProfile'
import {safeParseInt} from 'utils/ConstFunctions'
import {useTreatmentPlanFilter} from '@hooks/useTreatmentPlanFilter'
import {Pencil} from 'lucide-react'
import {canEditProductionBatch} from 'screens/Kanban/utils/productionBatchEditor'

interface BatchDetailsProps {
  processed: ProcessedManufacturingBatch
  index: number
  plan: AllTreatmentPlanListItem
  patientId: number
  latestManufacturingData?: ManufacturingItem | null
  readOnly?: boolean
}

type NonNullManufacturingStatus = Exclude<ManufacturingStatus, null>

export const manufacturingStatusMapper: Record<NonNullManufacturingStatus, string> = {
  MANUFACTURING: 'MANUFACTURING',
  PENDING: 'PENDING',
  MANUFACTURING_STARTED: 'In Production',
  IN_PROGRESS: 'IN PROGRESS',
  COMPLETED: 'COMPLETED',
  SHIPPED: 'SHIPPED',
  DELIVERED: 'DELIVERED',
  CANCELLED: 'CANCELLED',
}

const BatchDetails = (props: BatchDetailsProps) => {
  const {processed, plan, patientId, latestManufacturingData} = props
  const {profileId} = useContext(AuthContext)
  const navigate = useNavigate()
  const location = useLocation()
  const profileBasePath = useProfileBasePath()
  const {service_products, product_name} = processed || {}
  const {dispatchAction} = useDispatchAction()
  const {activeProfile} = useActiveProfile()
  const {isStarterPlanUser, isPastPlan} = useTreatmentPlanFilter()
  const [searchParams] = useSearchParams()

  const isOutsourced =
    Number(profileId) !== service_products?.profile_id &&
    service_products?.profile_id !== safeParseInt(activeProfile?.owner_profile_id)
  const {getIndividualTaskList} = useSelector((state: RootState) => state.workFlow)
  const isDeactivated = plan?.treatment_status === treatmentPlanStatusConstants.DEACTIVATED
  const isDelivered = processed?.status === 'DELIVERED'

  // Check if this is a past plan or previous order (disabled state)
  const isDisabledForPastPlan = isPastPlan

  // Get order_id from URL if present
  const orderIdFromUrl = searchParams.get('order_id')
  const treatmentIdFromUrl = searchParams.get('treatment_id')

  // Check if this is a previous order (enterprise)
  const {selectedOrderId} = useSelector((state: RootState) => state.customerPatientProfile)
  const {patientOrderList} = useSelector((state: RootState) => state.profile)

  const latestOrderId = useMemo(() => {
    const orders = (patientOrderList ?? [])
      .filter((order) => Boolean(order.order_id) && order.case_submitted === true)
      .sort(
        (a, b) =>
          new Date(a.order_creation_date ?? '').getTime() -
          new Date(b.order_creation_date ?? '').getTime()
      )
    return orders.length > 0 ? String(orders[orders.length - 1].order_id) : null
  }, [patientOrderList])

  const isLatestOrder = latestOrderId ? selectedOrderId === latestOrderId : true
  const isPreviousOrder = !isLatestOrder && orderIdFromUrl !== null

  // Determine if the view batch details button should be disabled
  const isViewDisabled = props.readOnly || isDeactivated || isDisabledForPastPlan || isPreviousOrder

  const handleEditBatch = () => {
    if (isViewDisabled || !canEditProductionBatch(processed)) return
    const params = new URLSearchParams({batch_id: String(processed.id), step: '0'})
    if (orderIdFromUrl) params.set('order_id', orderIdFromUrl)
    navigate(`/production-setup-stepper/${patientId}/${plan.aligner_treatment_id}?${params}`, {
      state: {returnTo: `${location.pathname}${location.search}`},
    })
  }

  const handleViewBatchDetails = () => {
    try {
      // Set instructions in Redux
      dispatchAction(setInstructions(processed?.instructions || []))

      // Store batch data in sessionStorage as backup
      sessionStorage.setItem(
        'batchDetailsData',
        JSON.stringify({
          processed,
          plan,
          batchId: processed?.id,
          treatmentPlanId: plan?.aligner_treatment_id,
          patientId: patientId,
          isReadOnly: isViewDisabled || isDisabledForPastPlan || isPreviousOrder,
        })
      )

      // Build navigation path and state
      let path = ''
      const stateData = {
        batchDetails: processed, // Use 'batchDetails' key to match what ProductionBatchDetails expects
        plan,
        isReadOnly: isViewDisabled || isDisabledForPastPlan || isPreviousOrder,
        batchId: processed?.id,
        treatmentPlanId: plan?.aligner_treatment_id,
        patientId: patientId,
      }

      // For starter plan users with past plans
      if (isStarterPlanUser && isPastPlan) {
        path = `/starter-plan-production/${patientId}/${plan?.aligner_treatment_id}/batch/${processed?.id}`
        if (treatmentIdFromUrl) {
          path += `?treatment_id=${treatmentIdFromUrl}`
        }
      }
      // For enterprise with previous orders
      else if (isPreviousOrder && orderIdFromUrl) {
        path = `${profileBasePath}/${patientId}/production/batch/${plan?.aligner_treatment_id}/${processed?.id}`
        // Add order_id as query param
        path += `?order_id=${orderIdFromUrl}`
        stateData.orderId = orderIdFromUrl
        stateData.isPreviousOrder = true
      }
      // Default path for active/latest plans
      else {
        path = `${profileBasePath}/${patientId}/production/batch/${plan?.aligner_treatment_id}/${processed?.id}`
      }

      // Navigate with state
      navigate(path, {state: stateData})
    } catch (error) {
      console.error('Error navigating to batch details:', error)
      // Fallback navigation - use a simpler path
      const fallbackPath = `/batch/${processed?.id}`
      navigate(fallbackPath, {
        state: {
          batchDetails: processed,
          plan,
          isReadOnly: true,
        },
      })
    }
  }

  const handleViewOngoingProduction = () => {
    try {
      const batchId = latestManufacturingData?.manufacturing_batch_id
      const params = new URLSearchParams()
      params.set('tab', 'ongoing')
      if (batchId) params.set('batchId', String(batchId))
      if (getIndividualTaskList?.patient_name)
        params.set('patientName', String(getIndividualTaskList?.patient_name))

      if (orderIdFromUrl) {
        params.set('order_id', orderIdFromUrl)
      }

      if (treatmentIdFromUrl) {
        params.set('treatment_id', treatmentIdFromUrl)
      }

      navigate(`/aligner-production?${params.toString()}`)
    } catch (error) {
      console.error('Error navigating to ongoing production:', error)
    }
  }

  // If processed data is missing, show a fallback UI
  if (!processed || !service_products) {
    return (
      <div className='p-4 bg-yellow-50 border border-yellow-200 rounded-lg'>
        <p className='text-yellow-700 font-medium'>Batch data is incomplete</p>
        <p className='text-sm text-gray-600 mt-1'>Please refresh the page or contact support.</p>
        <button
          onClick={() => window.location.reload()}
          className='mt-3 px-3 py-1 bg-primaryColor text-white rounded text-sm'
        >
          Refresh
        </button>
      </div>
    )
  }

  return (
    <>
      {!isViewDisabled && processed.id && canEditProductionBatch(processed) && (
        <div className='mb-3 flex justify-end'>
          <AntdButton
            type='default'
            text='Edit batch'
            icon={<Pencil className='h-4 w-4' aria-hidden='true' />}
            className='inline-flex items-center gap-1.5 text-primaryColor border-primaryColor'
            onClick={handleEditBatch}
          />
        </div>
      )}
      <div className='text-sm sm:text-base text-black'>
        <div className='mb-1'>
          <strong>Mode:</strong>{' '}
          {(isOutsourced ? modeOptions.Outsourced : modeOptions['In-House']).toUpperCase()}
        </div>
        <div className='mb-1'>
          <strong>Product:</strong> {product_name || '-'}
        </div>
        <div className='mb-1'>
          <strong>Started On:</strong>{' '}
          {hasValue(processed.started_on)
            ? dayjs(processed.started_on as any).format('DD-MMM-YYYY')
            : '-'}
        </div>
        <div className='mb-1'>
          <strong>Shipped On:</strong>{' '}
          {hasValue(processed.shipped_on)
            ? dayjs(processed.shipped_on as any).format('DD-MMM-YYYY')
            : '-'}
        </div>
        <div className='mb-3'>
          <strong>Delivered On:</strong>{' '}
          {hasValue(processed.delivered_on)
            ? dayjs(processed.delivered_on as any).format('DD-MMM-YYYY')
            : '-'}
        </div>
      </div>

      <div className='my-4 border border-mediumGray' />

      <div className='mt-3 sm:mt-4'>
        <div className='text-sm sm:text-base font-semibold mb-2'>Product Summary</div>
        <TableContainerForBatches processed={processed} />
      </div>

      <div className='mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2 sm:gap-3'>
        <div className='inline-block w-full sm:w-auto'>
          <AntdButton
            className='text-white bg-primaryColor border border-primaryColor font-semibold w-full sm:w-fit px-4 py-2 rounded-lg'
            text='View Batch Details >'
            htmlType='button'
            onClick={handleViewBatchDetails}
            disabled={false}
          />
        </div>

        {!isDelivered && !isViewDisabled && (
          <Tooltip title='Ongoing Production: Manage active batches currently under production.'>
            <div className='inline-block w-full sm:w-auto'>
              <AntdButton
                className='text-white bg-primaryColor border border-primaryColor font-semibold w-full sm:w-fit px-4 py-2 rounded-lg'
                text='View Ongoing Production'
                onClick={handleViewOngoingProduction}
              />
            </div>
          </Tooltip>
        )}
      </div>
    </>
  )
}

export default BatchDetails
